import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQueueStore } from "@/lib/store";
import { MasterPatient, Consultation, Patient } from "@/types";
import { ageToYears } from "@/lib/utils/ageUtils";
import { 
  Users, 
  TrendingUp, 
  Calendar, 
  MapPin, 
  UserCheck, 
  Clock,
  Repeat,
  Target,
  BarChart3,
  PieChart,
  Activity
} from "lucide-react";
import { safeFormatDateOnly } from '@/lib/utils/dateUtils';

export function PatientAnalytics() {
  const { 
    masterPatients, 
    fetchMasterPatients,
    consultations, 
    fetchConsultations,
    patients,
    fetchPatients
  } = useQueueStore();

  const [timeRange, setTimeRange] = useState("30");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchMasterPatients(),
      fetchConsultations(),
      fetchPatients()
    ]).finally(() => setIsLoading(false));
  }, [fetchMasterPatients, fetchConsultations, fetchPatients]);

  // Calculate date range
  const getDateRange = () => {
    const days = parseInt(timeRange);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();

  // Filter data by time range
  const filteredConsultations = consultations.filter(c => {
    const consultationDate = new Date(c.registeredAt);
    return consultationDate >= startDate && consultationDate <= endDate;
  });

  const filteredPatients = patients.filter(p => {
    const patientDate = new Date(p.registeredAt);
    return patientDate >= startDate && patientDate <= endDate;
  });

  // Analytics calculations
  const totalMasterPatients = masterPatients.length;
  const newPatientsInRange = filteredPatients.length;
  const totalConsultationsInRange = filteredConsultations.length;
  const completedConsultations = filteredConsultations.filter(c => c.status === 'completed').length;

  // Patient retention analysis
  const returningPatients = masterPatients.filter(mp => {
    const patientConsultations = consultations.filter(c => c.masterPatientId === mp.id);
    return patientConsultations.length > 1;
  });

  const retentionRate = totalMasterPatients > 0 ? (returningPatients.length / totalMasterPatients) * 100 : 0;

  // Visit frequency analysis
  const visitFrequency = masterPatients.map(mp => {
    const patientConsultations = consultations.filter(c => c.masterPatientId === mp.id);
    return {
      patientId: mp.id,
      patientName: mp.name,
      totalVisits: patientConsultations.length,
      lastVisit: patientConsultations.length > 0 ? 
        new Date(Math.max(...patientConsultations.map(c => new Date(c.registeredAt).getTime()))) : null
    };
  }).sort((a, b) => b.totalVisits - a.totalVisits);

  const topPatients = visitFrequency.slice(0, 5);

  // Demographic analysis
  const ageGroups = {
    '0-18': 0,
    '19-30': 0,
    '31-50': 0,
    '51-70': 0,
    '70+': 0
  };

  masterPatients.forEach(mp => {
    const ageInYears = ageToYears(mp.age);
    console.log(`Patient: ${mp.name}, Age: "${mp.age}", Calculated Years: ${ageInYears}`);
    
    if (ageInYears <= 18) ageGroups['0-18']++;
    else if (ageInYears <= 30) ageGroups['19-30']++;
    else if (ageInYears <= 50) ageGroups['31-50']++;
    else if (ageInYears <= 70) ageGroups['51-70']++;
    else ageGroups['70+']++;
  });

  const genderDistribution = {
    male: masterPatients.filter(mp => mp.gender === 'male').length,
    female: masterPatients.filter(mp => mp.gender === 'female').length,
    other: masterPatients.filter(mp => mp.gender === 'other').length
  };

  // Location analysis (excluding test data)
  const locationStats = masterPatients.reduce((acc, mp) => {
    if (mp.location && 
        !mp.location.toLowerCase().includes('test') && 
        !mp.location.toLowerCase().includes('demo') &&
        mp.location !== 'Test City') {
      acc[mp.location] = (acc[mp.location] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const topLocations = Object.entries(locationStats)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  // Recent activity
  const recentConsultations = consultations
    .sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime())
    .slice(0, 10);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hospital-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Patient Analytics</h2>
          <p className="text-gray-600">Comprehensive insights into patient behavior and patterns</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Total Patients</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{totalMasterPatients}</p>
            <p className="text-xs text-gray-500">Unique patients registered</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-600">New Patients</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{newPatientsInRange}</p>
            <p className="text-xs text-gray-500">In last {timeRange} days</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-gray-600">Retention Rate</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{retentionRate.toFixed(1)}%</p>
            <p className="text-xs text-gray-500">Returning patients</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-gray-600">Consultations</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{totalConsultationsInRange}</p>
            <p className="text-xs text-gray-500">{completedConsultations} completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Patients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Most Frequent Patients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topPatients.map((patient, index) => (
                <div key={patient.patientId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-hospital-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-hospital-primary">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium">{patient.patientName}</p>
                      <p className="text-sm text-gray-600">{patient.totalVisits} visits</p>
                    </div>
                  </div>
                  {patient.lastVisit && (
                    <div className="text-right text-sm text-gray-500">
                      Last: {safeFormatDateOnly(patient.lastVisit.toISOString())}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Demographic Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Demographics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Age Groups */}
              <div>
                <h4 className="font-medium mb-2">Age Distribution</h4>
                <div className="space-y-2">
                  {Object.entries(ageGroups).map(([range, count]) => (
                    <div key={range} className="flex items-center justify-between">
                      <span className="text-sm">{range} years</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-hospital-primary h-2 rounded-full" 
                            style={{ width: `${(count / totalMasterPatients) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gender Distribution */}
              <div>
                <h4 className="font-medium mb-2">Gender Distribution</h4>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{genderDistribution.male}</div>
                    <div className="text-sm text-gray-600">Male</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-pink-600">{genderDistribution.female}</div>
                    <div className="text-sm text-gray-600">Female</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{genderDistribution.other}</div>
                    <div className="text-sm text-gray-600">Other</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Top Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topLocations.map(([location, count], index) => (
                <div key={location} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-hospital-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-hospital-primary">{index + 1}</span>
                    </div>
                    <span className="font-medium">{location}</span>
                  </div>
                  <Badge variant="outline">{count} patients</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentConsultations.map((consultation) => {
                const masterPatient = masterPatients.find(mp => mp.id === consultation.masterPatientId);
                return (
                  <div key={consultation.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <p className="font-medium">{masterPatient?.name || 'Unknown'}</p>
                      <p className="text-sm text-gray-600">{consultation.reason}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{safeFormatDateOnly(consultation.registeredAt)}</p>
                      <Badge 
                        variant={consultation.status === 'completed' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {consultation.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 